import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useMasterData } from "@/hooks/use-master-data";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * Bulk-action dialogs shared by the Leads list and detailed-filters pages.
 *
 * Every dialog takes the same selection contract and builds the standard payload:
 *   selection = { lead_ids?, select_all?, filters }
 * where the parent passes `buildSelection()` → returns that object based on whether
 * rows are checked or "all filtered" is active.
 *
 * Common props: { open, onOpenChange, buildSelection, count, onDone }.
 */

// Row-tint palette for "Set Lead Color" — light backgrounds that read well as a
// full table-row tint. `null` clears the color. Kept small and meaningful.
export const LEAD_COLOR_OPTIONS = [
  { value: null,          label: "None",   swatch: "bg-white border" },
  { value: "red",         label: "Red",    swatch: "bg-red-200" },
  { value: "amber",       label: "Amber",  swatch: "bg-amber-200" },
  { value: "green",       label: "Green",  swatch: "bg-green-200" },
  { value: "blue",        label: "Blue",   swatch: "bg-blue-200" },
  { value: "purple",      label: "Purple", swatch: "bg-purple-200" },
  { value: "pink",        label: "Pink",   swatch: "bg-pink-200" },
  { value: "gray",        label: "Gray",   swatch: "bg-gray-200" },
];

// Map a stored color token → the row background class used in the table.
export const LEAD_COLOR_BG = {
  red: "bg-red-100 hover:bg-red-200/70",
  amber: "bg-amber-100 hover:bg-amber-200/70",
  green: "bg-green-100 hover:bg-green-200/70",
  blue: "bg-blue-100 hover:bg-blue-200/70",
  purple: "bg-purple-100 hover:bg-purple-200/70",
  pink: "bg-pink-100 hover:bg-pink-200/70",
  gray: "bg-gray-100 hover:bg-gray-200/70",
};

// Fields the "Update Field" dialog can set (mirrors backend BULK_EDITABLE_FIELDS,
// minus status which has its own dialog). Each entry says how to render the value.
const FIELD_DEFS = [
  { key: "source",             label: "Source",             kind: "md", md: "sources" },
  { key: "sub_source",         label: "Sub Source",         kind: "md", md: "subSources" },
  { key: "stage",              label: "Stage (temp)",       kind: "md", md: "stages" },
  { key: "category",           label: "Category",           kind: "md", md: "categories" },
  { key: "concern",            label: "Concern",            kind: "md", md: "concerns" },
  { key: "interested_service", label: "Interested Service", kind: "text" },
  { key: "city",               label: "City",               kind: "text" },
  { key: "state",              label: "State",              kind: "text" },
  { key: "remark",             label: "Remark",             kind: "text" },
];

function useSubmit(onDone, onOpenChange) {
  const [saving, setSaving] = useState(false);
  const run = async (fn, successMsg) => {
    setSaving(true);
    try {
      const res = await fn();
      toast.success(typeof successMsg === "function" ? successMsg(res?.data) : successMsg);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return { saving, run };
}

// ── Update Status ────────────────────────────────────────────────────────────
export function BulkStatusDialog({ open, onOpenChange, buildSelection, count, onDone }) {
  const { statuses } = useMasterData();
  const [status, setStatus] = useState("");
  const { saving, run } = useSubmit(onDone, onOpenChange);
  const submit = () =>
    run(() => api.post("/leads/bulk-status", { ...buildSelection(), status }),
        (d) => `Updated ${d?.updated ?? 0} lead(s)${d?.locked ? ` · ${d.locked} locked` : ""}`);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Update Stage — {count} lead(s)</DialogTitle></DialogHeader>
        <div className="space-y-2 py-2">
          <Label>New status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="bulk-status-select"><SelectValue placeholder="Choose status" /></SelectTrigger>
            <SelectContent>
              {(statuses || []).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Appointment-booked leads are skipped (locked until visited).</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!status || saving} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? "Updating…" : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Update Field (generic) ───────────────────────────────────────────────────
export function BulkFieldDialog({ open, onOpenChange, buildSelection, count, onDone, initialField }) {
  const md = useMasterData();
  const [field, setField] = useState(initialField || "source");
  const [value, setValue] = useState("");
  const { saving, run } = useSubmit(onDone, onOpenChange);

  // reset value when field changes, and honor an incoming initialField each open
  React.useEffect(() => { if (open) { setField(initialField || "source"); setValue(""); } }, [open, initialField]);

  const def = FIELD_DEFS.find((f) => f.key === field) || FIELD_DEFS[0];
  const mdOptions = (key) => {
    const raw = md[key] || [];
    return raw.map((o) => (typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label || o.value }));
  };
  const submit = () =>
    run(() => api.post("/leads/bulk-field", { ...buildSelection(), field, value }),
        (d) => `Updated ${d?.updated ?? 0} lead(s)`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Update Field — {count} lead(s)</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label>Field</Label>
            <Select value={field} onValueChange={(v) => { setField(v); setValue(""); }}>
              <SelectTrigger data-testid="bulk-field-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FIELD_DEFS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>New value</Label>
            {def.kind === "md" ? (
              <Select value={value} onValueChange={setValue}>
                <SelectTrigger data-testid="bulk-field-value"><SelectValue placeholder="Choose value" /></SelectTrigger>
                <SelectContent>
                  {mdOptions(def.md).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Enter value" data-testid="bulk-field-value" />
            )}
            <p className="text-xs text-muted-foreground">Empty value clears the field.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? "Updating…" : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Set Lead Color ───────────────────────────────────────────────────────────
export function BulkColorDialog({ open, onOpenChange, buildSelection, count, onDone }) {
  const [color, setColor] = useState(null);
  const { saving, run } = useSubmit(onDone, onOpenChange);
  const submit = () =>
    run(() => api.post("/leads/bulk-color", { ...buildSelection(), color }),
        (d) => `Updated ${d?.updated ?? 0} lead(s)`);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Set Lead Color — {count} lead(s)</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2 py-3">
          {LEAD_COLOR_OPTIONS.map((c) => (
            <button
              key={c.label}
              onClick={() => setColor(c.value)}
              className={`flex h-10 w-10 items-center justify-center rounded-md ${c.swatch} ${color === c.value ? "ring-2 ring-navy ring-offset-2" : ""}`}
              title={c.label}
              data-testid={`bulk-color-${c.label}`}
            />
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? "Applying…" : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Update Branch ────────────────────────────────────────────────────────────
export function BulkBranchDialog({ open, onOpenChange, buildSelection, count, onDone }) {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const { saving, run } = useSubmit(onDone, onOpenChange);

  React.useEffect(() => {
    if (!open) return;
    setBranchId("");
    api.get("/branches").then((r) => {
      let list = r.data || [];
      // A branch manager may only move leads into a branch they manage.
      if ((user?.branch_ids || []).length && user?.role === "branch_manager") {
        list = list.filter((b) => (user.branch_ids || []).includes(b.id));
      }
      setBranches(list);
    }).catch(() => {});
  }, [open, user]);

  const submit = () =>
    run(() => api.post("/leads/bulk-branch", { ...buildSelection(), branch_id: branchId }),
        (d) => `Moved ${d?.updated ?? 0} lead(s)`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Update Branch — {count} lead(s)</DialogTitle></DialogHeader>
        <div className="space-y-2 py-2">
          <Label>New branch</Label>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger data-testid="bulk-branch-select"><SelectValue placeholder="Choose branch" /></SelectTrigger>
            <SelectContent>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Franchise is updated automatically to match the branch.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!branchId || saving} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? "Updating…" : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Set / Reset Next Follow-up ───────────────────────────────────────────────
export function BulkFollowupDialog({ open, onOpenChange, buildSelection, count, onDone, mode = "set" }) {
  const [date, setDate] = useState("");
  const { saving, run } = useSubmit(onDone, onOpenChange);
  React.useEffect(() => { if (open) setDate(""); }, [open]);
  const isReset = mode === "reset";
  const submit = () =>
    run(() => api.post("/leads/bulk-followup", {
      ...buildSelection(), mode, date: isReset ? null : (date ? new Date(date).toISOString() : null),
    }), (d) => `${isReset ? "Reset" : "Set"} follow-up on ${d?.updated ?? 0} lead(s)`);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{isReset ? "Reset" : "Set"} Next Follow-up — {count} lead(s)</DialogTitle></DialogHeader>
        {isReset ? (
          <p className="py-2 text-sm text-muted-foreground">This clears the next follow-up and cancels any pending follow-ups on the selected leads.</p>
        ) : (
          <div className="space-y-1.5 py-2">
            <Label>Follow-up date &amp; time</Label>
            <Input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="bulk-followup-date" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || (!isReset && !date)} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? "Saving…" : isReset ? "Reset" : "Set"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Add To Today's List (with a time window) ─────────────────────────────────
const TODAYS_LIST_DURATIONS = [
  { value: "2h",   label: "2 hours" },
  { value: "4h",   label: "4 hours" },
  { value: "6h",   label: "6 hours" },
  { value: "eod",  label: "End of day" },
  { value: "none", label: "No expiry" },
];

export function BulkTodaysListDialog({ open, onOpenChange, buildSelection, count, onDone, mode = "add" }) {
  const [duration, setDuration] = useState("2h");
  const [targetUser, setTargetUser] = useState("");
  const [users, setUsers] = useState([]);
  const { saving, run } = useSubmit(onDone, onOpenChange);
  const isRemove = mode === "remove";

  // Load the users a manager can hand leads to (telecallers do the calling).
  React.useEffect(() => {
    if (!open) return;
    setDuration("2h");
    setTargetUser("");
    api.get("/users", { params: { role: "telecaller" } })
      .then((r) => setUsers(r.data || []))
      .catch(() => {});
  }, [open]);

  const submit = () =>
    run(() => api.post("/leads/bulk-todays-list", {
      ...buildSelection(), action: mode, target_user_id: targetUser,
      ...(isRemove ? {} : { duration }),
    }), (d) => isRemove
      ? `Removed ${d?.updated ?? 0} lead(s) from the list`
      : `Gave ${d?.updated ?? 0} lead(s) to work in Today's List`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isRemove ? "Remove From Today's List" : "Give Leads to Work"} — {count} lead(s)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label>{isRemove ? "Remove from whose list" : "Assign to work (Today's List of)"}</Label>
            <Select value={targetUser} onValueChange={setTargetUser}>
              <SelectTrigger data-testid="today-user"><SelectValue placeholder="Choose a user" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!isRemove && (
            <div className="space-y-1.5">
              <Label>Keep on their list for</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger data-testid="today-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TODAYS_LIST_DURATIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!isRemove && (
            <p className="text-xs text-muted-foreground">
              Leads appear on that user's Today's List to work — <b>ownership doesn't change</b> — and drop off automatically when the window ends. They'll be notified.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !targetUser} className={isRemove ? "bg-red-600 text-white hover:bg-red-700" : "bg-navy text-white hover:bg-navy-hover"}>
            {saving ? "Working…" : isRemove ? "Remove" : "Give to work"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Bulk WhatsApp (template) ─────────────────────────────────────────────────
export function BulkWhatsAppDialog({ open, onOpenChange, buildSelection, count, onDone }) {
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(false);
  const { saving, run } = useSubmit(onDone, onOpenChange);

  React.useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/jalpi/templates")
      .then((r) => setTemplates(r.data || []))
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));
  }, [open]);

  const submit = () =>
    run(() => api.post("/leads/bulk-whatsapp", { ...buildSelection(), template_name: templateName, language }),
        (d) => `WhatsApp: ${d?.sent ?? 0} sent, ${d?.failed ?? 0} failed, ${d?.skipped ?? 0} skipped`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Send WhatsApp — {count} lead(s)</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label>Template</Label>
            <Select value={templateName} onValueChange={(v) => {
              setTemplateName(v);
              const t = templates.find((x) => x.name === v);
              if (t?.language) setLanguage(t.language);
            }}>
              <SelectTrigger data-testid="bulk-wa-template"><SelectValue placeholder={loading ? "Loading…" : "Choose template"} /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={`${t.name}:${t.language}`} value={t.name}>
                    {t.name} {t.language ? `(${t.language})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
            This sends a real WhatsApp message to <b>{count}</b> lead(s). Only approved templates
            can be sent; leads without a number are skipped.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!templateName || saving} className="bg-green-600 text-white hover:bg-green-700">
            {saving ? "Sending…" : `Send to ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
